// src/components/Dashboard/Inventory/Inventory_Dashboard.js
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import { typography } from '../../../styles/typography.js';
import Card from '../../ui/card.js';
import { inventoryDashboardApi } from './Inventory_API.js';
import ItemsWithHighestMaintenance from './Items_with_Highest_Maintenance.js';
import MaintenanceCost from './Maintenance_Cost.js';
import MaintenanceVsTransportationCost from './Maintenance_vs_Transportation_Cost.js';
import NewVsRetiredItems from './New_vs_Retired_Items.js';
import TypeBreakdown from './Type_Breakdown.js';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Filler,
  Tooltip,
  Legend
);

const MAINTENANCE_COLOR_PALETTE = [
  '#9333EA',
  '#14B8A6',
  '#2563EB',
  '#EF4444',
  '#F59E0B',
  '#06B6D4',
  '#8B5CF6',
  '#F472B6',
];

const getCurrentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const Inventory_Dashboard = () => {
  // Log screen view
  useScreenLogging('InventoryDashboard', {
    component: 'InventoryDashboard',
  });
  const [summaryData, setSummaryData] = useState({
    totalItems: 0,
    itemsInGoodCondition: 0,
    itemsInMaintenance: 0,
  });
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthValue());
  const [maintenanceStats, setMaintenanceStats] = useState([]);
  const [isMaintenanceLoading, setIsMaintenanceLoading] = useState(true);
  const [maintenanceError, setMaintenanceError] = useState(null);
  const [selectedRadarDataset, setSelectedRadarDataset] = useState(null);

  const highestMaintenanceData = useMemo(() => {
    if (!maintenanceStats.length) return [];

    return maintenanceStats
      .map(stat => {
        const statusBreakdown = stat?.status_breakdown || {};
        const completed = Number(statusBreakdown?.completed) || 0;
        const inProgress = Number(statusBreakdown?.in_progress) || 0;
        const maintenanceCount = Number(stat?.maintenance_count);
        const value =
          Number.isFinite(maintenanceCount) && maintenanceCount > 0
            ? maintenanceCount
            : completed + inProgress;

        return {
          label: stat?.category || 'Unknown',
          value,
        };
      })
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 3)
      .map((item, index) => ({
        ...item,
        color:
          MAINTENANCE_COLOR_PALETTE[index % MAINTENANCE_COLOR_PALETTE.length],
      }));
  }, [maintenanceStats]);
  const maintenanceCostTableData = useMemo(() => {
    if (!maintenanceStats.length) return [];

    return maintenanceStats.map(stat => {
      const type = stat?.category || 'Unknown';
      const statusBreakdown = stat?.status_breakdown || {};
      const doneMaintenance = statusBreakdown?.completed ?? 0;
      const underMaintenance = statusBreakdown?.in_progress ?? 0;
      const charges = Number(stat?.total_charges) || 0;
      const transportation = Number(stat?.total_transportation_charges) || 0;
      return {
        type,
        doneMaintenance,
        underMaintenance,
        charges,
        transportation,
        totalCharges: charges + transportation,
      };
    });
  }, [maintenanceStats]);

  const maintenanceCostChartData = useMemo(() => {
    if (!maintenanceStats.length) {
      return {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: [],
            borderColor: '#ffffff',
            borderWidth: 2,
            hoverOffset: 6,
          },
        ],
      };
    }

    return {
      labels: maintenanceStats.map(stat => stat?.category || 'Unknown'),
      datasets: [
        {
          data: maintenanceStats.map(stat =>
            Number(stat?.percentage_of_total) || 0
          ),
          backgroundColor: maintenanceStats.map(
            (_, index) =>
              MAINTENANCE_COLOR_PALETTE[
                index % MAINTENANCE_COLOR_PALETTE.length
              ]
          ),
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    };
  }, [maintenanceStats]);

  const maintenanceCostChartOptions = useMemo(() => {
    return {
      cutout: '55%',
      plugins: {
        tooltip: {
          callbacks: {
            label: context => {
              const value = context.parsed ?? 0;
              return `${context.label}: ${value.toFixed(1)}%`;
            },
          },
        },
        legend: {
          display: false,
        },
      },
      responsive: true,
      maintainAspectRatio: false,
    };
  }, []);

  const maintenanceVsTransportationCategoryData = useMemo(() => {
    if (!maintenanceStats.length) return [];

    return maintenanceStats.map(stat => {
      const category = stat?.category || 'Unknown';
      const averageMaintenance =
        Number(stat?.average_maintenance_cost) ||
        Number(stat?.total_charges) ||
        0;
      const averageTransportation =
        Number(stat?.average_transportation_per_maintenance) ||
        Number(stat?.total_transportation_charges) ||
        0;

      return {
        type: category,
        averageMaintenance,
        averageTransportation,
        combinedAverage: averageMaintenance + averageTransportation,
      };
    });
  }, [maintenanceStats]);

  const maintenanceVsTransportationRadarData = useMemo(() => {
    if (!maintenanceVsTransportationCategoryData.length) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const maintenanceValues = maintenanceVsTransportationCategoryData.map(
      item => item.averageMaintenance
    );
    const transportationValues = maintenanceVsTransportationCategoryData.map(
      item => item.averageTransportation
    );

    const maxMaintenance = Math.max(...maintenanceValues, 0);
    const maxTransportation = Math.max(...transportationValues, 0);

    const normalize = (value, max) =>
      max > 0 ? (value / max) * 100 : 0;

    const isMaintenanceSelected =
      !selectedRadarDataset || selectedRadarDataset === 'maintenance';
    const isTransportationSelected =
      !selectedRadarDataset || selectedRadarDataset === 'transportation';

    return {
      labels: maintenanceVsTransportationCategoryData.map(item => item.type),
      datasets: [
        {
          label: 'Maintenance per Item',
          data: maintenanceValues.map(value =>
            normalize(value, maxMaintenance)
          ),
          backgroundColor: `rgba(151, 71, 255, ${isMaintenanceSelected ? 0.25 : 0.08})`,
          borderColor: isMaintenanceSelected ? '#9747FF' : 'rgba(151, 71, 255, 0.35)',
          pointBackgroundColor: '#9747FF',
          pointBorderColor: '#FFFFFF',
          pointBorderWidth: isMaintenanceSelected ? 2.5 : 1.5,
          pointRadius: isMaintenanceSelected ? 4 : 3,
          borderWidth: isMaintenanceSelected ? 2.5 : 1.25,
          fill: true,
        },
        {
          label: 'Transportation per Maintenance',
          data: transportationValues.map(value =>
            normalize(value, maxTransportation)
          ),
          backgroundColor: `rgba(101, 205, 105, ${isTransportationSelected ? 0.25 : 0.08})`,
          borderColor: isTransportationSelected ? '#65CD69' : 'rgba(101, 205, 105, 0.35)',
          pointBackgroundColor: '#65CD69',
          pointBorderColor: '#FFFFFF',
          pointBorderWidth: isTransportationSelected ? 2.5 : 1.5,
          pointRadius: isTransportationSelected ? 4 : 3,
          borderWidth: isTransportationSelected ? 2.5 : 1.25,
          fill: true,
        },
      ],
    };
  }, [maintenanceVsTransportationCategoryData, selectedRadarDataset]);

  const maintenanceVsTransportationRadarOptions = useMemo(() => {
    return {
      plugins: {
        tooltip: {
          callbacks: {
            label: context => {
              const index = context.dataIndex ?? 0;
              const category = maintenanceVsTransportationCategoryData[index];
              if (!category) {
                const value = context.parsed ?? 0;
                return `${context.dataset.label}: ${Number(value).toFixed(1)}%`;
              }

              const actualValue =
                context.datasetIndex === 0
                  ? category.averageMaintenance
                  : category.averageTransportation;
              return `${context.dataset.label}: ₹${Number(actualValue).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`;
            },
          },
        },
        legend: {
          position: 'bottom',
          labels: {
            color: '#0F172A',
            usePointStyle: true,
          },
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          beginAtZero: true,
          angleLines: {
            color: 'rgba(15, 23, 42, 0.15)',
          },
          grid: {
            color: 'rgba(15, 23, 42, 0.1)',
          },
          pointLabels: {
            font: {
              size: 12,
              family: 'inherit',
            },
            color: '#0F172A',
          },
          ticks: {
            showLabelBackdrop: false,
            color: '#475569',
            callback: value => `${Number(value).toFixed(0)}%`,
            maxTicksLimit: 5,
          },
        },
      },
    };
  }, [maintenanceVsTransportationCategoryData]);

  const handleRadarDatasetToggle = datasetKey => {
    setSelectedRadarDataset(prev =>
      prev === datasetKey ? null : datasetKey
    );
  };

  const maintenanceVsTransportationSummary = useMemo(() => {
    if (!maintenanceVsTransportationCategoryData.length) {
      return {
        averageMaintenance: 0,
        averageTransportation: 0,
        maintenanceShare: 0,
        transportationShare: 0,
      };
    }

    const totals = maintenanceVsTransportationCategoryData.reduce(
      (acc, item) => {
        acc.averageMaintenance += item.averageMaintenance;
        acc.averageTransportation += item.averageTransportation;
        return acc;
      },
      { averageMaintenance: 0, averageTransportation: 0 }
    );

    const count = maintenanceVsTransportationCategoryData.length || 1;
    const averageMaintenance = totals.averageMaintenance / count;
    const averageTransportation = totals.averageTransportation / count;
    const total = averageMaintenance + averageTransportation || 1;

    return {
      averageMaintenance,
      averageTransportation,
      maintenanceShare: (averageMaintenance / total) * 100,
      transportationShare: (averageTransportation / total) * 100,
    };
  }, [maintenanceVsTransportationCategoryData]);
  const fetchSummaryData = async () => {
    setIsSummaryLoading(true);
    setSummaryError(null);
    try {
      const now = new Date();
      const response = await inventoryDashboardApi.getInventoryDashboard({
        month: String(now.getMonth() + 1),
        year: String(now.getFullYear()),
      });

      if (response?.success && response.data) {
        const summary = response.data.summary;
        const legacySummary = response.data.inventory_summary;

        if (summary || legacySummary) {
          const parsedSummary = {
            totalItems: summary?.total_items ?? legacySummary?.total_items ?? 0,
            itemsInGoodCondition:
              summary?.items_in_good_condition ??
              legacySummary?.status_breakdown?.good_condition ??
              0,
            itemsInMaintenance:
              summary?.items_in_maintenance ??
              legacySummary?.status_breakdown?.under_maintenance ??
              0,
          };

          setSummaryData(parsedSummary);
        } else {
          setSummaryError('Inventory summary data not available.');
        }
      } else {
        setSummaryError(
          response?.error || 'Failed to fetch inventory dashboard summary.'
        );
      }
    } catch (error) {
      setSummaryError(
        error?.message || 'Unexpected error while fetching inventory summary.'
      );
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const parseSelectedMonth = () => {
    if (!selectedMonth || typeof selectedMonth !== 'string') {
      return null;
    }

    const [yearStr, monthStr] = selectedMonth.split('-');
    if (!yearStr || !monthStr) return null;

    const normalizedMonth = String(Number(monthStr));
    const normalizedYear = Number(yearStr);

    if (!normalizedMonth || Number.isNaN(Number(normalizedMonth))) return null;
    if (Number.isNaN(normalizedYear)) return null;

    return {
      month: normalizedMonth,
      year: normalizedYear,
    };
  };

  const fetchMaintenanceStatistics = useCallback(async () => {
    const parsedMonth = parseSelectedMonth();

    if (!parsedMonth) {
      setMaintenanceStats([]);
      setMaintenanceError('Please select a valid month to view maintenance data.');
      setIsMaintenanceLoading(false);
      return;
    }

    setIsMaintenanceLoading(true);
    setMaintenanceError(null);

    try {
      const response = await inventoryDashboardApi.getMaintenanceStatistics({
        month: parsedMonth.month,
        year: parsedMonth.year,
      });

      if (response?.success && response.data) {
        const stats = Array.isArray(response.data?.maintenance_stats)
          ? response.data.maintenance_stats
          : [];

        setMaintenanceStats(stats);
      } else {
        setMaintenanceStats([]);
        setMaintenanceError(
          response?.error || 'Failed to fetch maintenance statistics.'
        );
      }
    } catch (error) {
      setMaintenanceStats([]);
      setMaintenanceError(
        error?.message || 'Failed to fetch maintenance statistics.'
      );
    } finally {
      setIsMaintenanceLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchSummaryData();
  }, []);

  useEffect(() => {
    fetchMaintenanceStatistics();
  }, [fetchMaintenanceStatistics]);

  const renderSummaryValue = value => {
    if (isSummaryLoading) return '...';
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return '0';
    }
    return numericValue.toLocaleString();
  };

  return (
    <div
      className="w-full h-full"
      style={{
        // background: 'linear-gradient(135deg, #E0F2F7 0%, #B8E6F0 100%)',
        minHeight: '100vh',
      }}
    >
      <div className="max-w mx-auto">
        <div className="flex flex-col lg:flex-row gap-6 mb-14">
          {/* Left Section - Total Item Inventory */}
          <div className="flex flex-col gap-4 w-full lg:w-[320px] flex-shrink-0">
            <h2
              className={typography.h3}
              style={{ color: '#000000', marginBottom: '0.5rem' }}
            >
              Total Item Inventory
            </h2>

            {/* Total Item Inventory Card */}
            <Card.Gradient className="p-4">
              <div className="flex items-center justify-between w-full gap-4">
                <p
                  className={typography.p}
                  style={{
                    color: '#666666',
                    fontSize: '0.95rem',
                    flex: 1,
                  }}
                >
                  Total Item Inventory
                </p>

                <p
                  className={typography.h2}
                  style={{
                    color: '#000000',
                    fontSize: '2.25rem',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}
                >
                  {renderSummaryValue(summaryData.totalItems)}
                </p>
              </div>
            </Card.Gradient>

            {/* Items in good condition Card */}
            <Card.Gradient className="p-4">
              <div className="flex items-center justify-between w-full gap-4">
                <p
                  className={typography.p}
                  style={{
                    color: '#666666',
                    fontSize: '0.95rem',
                    flex: 1,
                  }}
                >
                  Items in good condition
                </p>
                <p
                  className={typography.h2}
                  style={{
                    color: '#000000',
                    fontSize: '2.25rem',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}
                >
                  {renderSummaryValue(summaryData.itemsInGoodCondition)}
                </p>
              </div>
            </Card.Gradient>

            {/* Items in maintenance Card */}
            <Card.Gradient className="p-4">
              <div className="flex items-center justify-between w-full gap-4">
                <p
                  className={typography.p}
                  style={{
                    color: '#666666',
                    fontSize: '0.95rem',
                    flex: 1,
                  }}
                >
                  Items in maintenance
                </p>
                <p
                  className={typography.h2}
                  style={{
                    color: '#000000',
                    fontSize: '2.25rem',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}
                >
                  {renderSummaryValue(summaryData.itemsInMaintenance)}
                </p>
              </div>
            </Card.Gradient>

            {summaryError && (
              <p className="text-sm text-red-600">{summaryError}</p>
            )}
          </div>

          {/* Right Section - Type Breakdown */}
          <TypeBreakdown />
        </div>

        <div className="mt-10">
          <div className="relative w-full max-w-lg">
            <label className="block mb-2 text-sm font-semibold text-neutral-700">
              Filter by Month
            </label>
            <input
              type="month"
              className={`block w-full h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] backdrop-blur-[30px] text-lg font-bold font-['Figtree'] leading-snug ${
                selectedMonth
                  ? 'outline-green-700 text-green-700'
                  : 'outline-white text-neutral-900'
              }`}
              value={selectedMonth}
              onChange={event => setSelectedMonth(event.target.value)}
            />
          </div>

          <div className="mt-8 grid grid-cols-1 xl:grid-cols-2 gap-6">
            <NewVsRetiredItems selectedMonth={selectedMonth} />
            <ItemsWithHighestMaintenance
              data={highestMaintenanceData}
              isLoading={isMaintenanceLoading}
              error={maintenanceError}
              onRetry={fetchMaintenanceStatistics}
            />
          </div>

          <MaintenanceCost
            isLoading={isMaintenanceLoading}
            error={maintenanceError}
            stats={maintenanceStats}
            chartData={maintenanceCostChartData}
            chartOptions={maintenanceCostChartOptions}
            tableData={maintenanceCostTableData}
            onRetry={fetchMaintenanceStatistics}
          />

          <MaintenanceVsTransportationCost
            isLoading={isMaintenanceLoading}
            error={maintenanceError}
            categoryData={maintenanceVsTransportationCategoryData}
            summary={maintenanceVsTransportationSummary}
            radarData={maintenanceVsTransportationRadarData}
            radarOptions={maintenanceVsTransportationRadarOptions}
            selectedDataset={selectedRadarDataset}
            onToggleDataset={handleRadarDatasetToggle}
            onRetry={fetchMaintenanceStatistics}
          />
        </div>
      </div>
    </div>
  );
};

export default Inventory_Dashboard;

